import React, { Component, type ReactElement, type MouseEvent } from 'react';
// eslint-disable-next-line jira/restricted/prop-types
import PropTypes from 'prop-types';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import noop from 'lodash/noop';
// @ts-expect-error - TS2724 - '"react-beautiful-dnd"' has no exported member named 'DragHandleProps'. Did you mean 'DraggableProps'?
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { Draggable, type DragHandleProps, type DraggableStateSnapshot } from 'react-beautiful-dnd';
import { colors, noFocusRing } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DraggableCardContainer = styled.div`
	&:last-child > div {
		${noFocusRing};
		border-bottom-color: transparent;
	}
`;

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const clearBorderMixin = css`
	&:last-child {
		${noFocusRing};
		border-bottom-color: transparent;
	}
`;

const withFixedHeight = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 5}px`,
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Card = styled.div<{
	isDragging: boolean;
	isInteractive: boolean;
	isVariableHeight: boolean;
	isDraggable: boolean;
	isHovering: boolean;
	isActive: boolean;
}>`
	background: ${token('color.background.neutral.subtle', colors.N0)};
	border-radius: '4px';
	cursor: ${({ isDragging, isInteractive }) => {
		if (isInteractive) {
			return isDragging ? 'grabbing' : 'pointer';
		}
		return 'auto';
	}};
	display: flex;
	position: relative;
	${({ isVariableHeight }) => !isVariableHeight && withFixedHeight};
	padding: 0 ${token('space.075', '6px')};
	margin: 0 ${token('space.025', '2px')};

	${({ isDragging }) =>
		css({
			borderBottom: `1px solid ${
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				isDragging
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						token('utility.UNSAFE.transparent', colors.N30)
					: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						token('color.border', colors.N30)
			}`,
		})}

	${({ isDraggable }) => !isDraggable && clearBorderMixin};

	${({ isHovering }) =>
		isHovering &&
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			background: token('color.background.neutral.subtle.hovered', colors.N30),
			textDecoration: 'none',
		})};

	${({ isActive, isDragging }) =>
		isActive &&
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			background: isDragging
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('elevation.surface.overlay', colors.B50)
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					token('color.background.neutral.subtle.pressed', colors.B50),
			boxShadow: token('elevation.shadow.overlay', 'none'),
		})}

	&:focus {
		z-index: 1;
		outline: none;
		box-shadow: ${token('color.border.focused', colors.B200)} 0 0 0 2px;
	}
`;

const isMiddleClick = (event: MouseEvent<HTMLElement> | MouseEvent<HTMLElement, MouseEvent>) =>
	event.button === 1;

export type Item = {
	readonly id: string;
};

export type ItemRenderer<T> = (
	isHovering: boolean,
	isActive: boolean,
	isFocused: boolean,
	item: T,
) => ReactElement;

type Props<T> = {
	item: T;
	index: number;
	isReorderEnabled: boolean;
	isInteractive: boolean;
	isVariableHeight: boolean;
	children: ItemRenderer<T>;
	onClick: (item: T, arg2: MouseEvent<HTMLElement>) => void;
};

type State = {
	isHovering: boolean;
	isActive: boolean;
	isFocused: boolean;
};

type EventHandlers = {
	onBlur: () => void;
	onClick: (arg1: MouseEvent<HTMLElement>) => void;
	onDragEnd: () => void;
	onFocus: () => void;
	onMouseEnter: () => void;
	onMouseLeave: (arg1: MouseEvent<HTMLElement>) => void;
	onMouseDown: (arg1: MouseEvent<HTMLElement>) => void;
	onMouseUp: () => void;
};

// eslint-disable-next-line jira/react/no-class-components
export default class ItemLineCard<T extends Item> extends Component<Props<T>, State> {
	static propTypes = {
		item: PropTypes.object.isRequired,
		index: PropTypes.number.isRequired,
		isReorderEnabled: PropTypes.bool,
		isInteractive: PropTypes.bool,
		isVariableHeight: PropTypes.bool,
		children: PropTypes.func.isRequired,
		onClick: PropTypes.func,
	};

	static defaultProps = {
		isReorderEnabled: false,
		isInteractive: true,
		isVariableHeight: false,
		onClick: noop,
	};

	state = {
		isHovering: false,
		isActive: false,
		isFocused: false,
	};

	eventHandlers: EventHandlers = {
		onBlur: () => {
			this.setState({ isFocused: false });
		},
		onClick: (event: MouseEvent<HTMLElement>) => {
			// best practice to use for react-beautiful-dnd (https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/how-we-use-dom-events.md)
			if (event.defaultPrevented === true) {
				return;
			}

			// Middle clicks are handled in onMouseDown for cross browser support.
			if (!isMiddleClick(event)) {
				this.propagateClick(event);
			}
		},
		onDragEnd: () => this.setState({ isActive: false }),
		onFocus: () => {
			this.setState({ isFocused: true });
		},
		onMouseEnter: () => this.setState({ isHovering: true }),
		onMouseLeave: () => this.setState({ isHovering: false, isActive: false }),
		onMouseDown: (event: MouseEvent<HTMLElement>) => {
			if (isMiddleClick(event)) {
				this.propagateClick(event);
			}
			this.setState({ isActive: true });
		},
		onMouseUp: () => this.setState({ isActive: false }),
	};

	propagateClick = (event: MouseEvent<HTMLElement>) => {
		this.props.onClick(this.props.item, event);
	};

	patchedHandlers = (
		dragHandleProps: DragHandleProps | null | undefined,
		snapshot: DraggableStateSnapshot,
	) => {
		/* The 'isActive' state is determined by the draggable state,
           i.e. if isDragging then the state is considered active. */
		const onMouseDown = (event: MouseEvent<HTMLElement>) => {
			if (isMiddleClick(event)) {
				this.propagateClick(event);
			}
		};

		const onClick = !dragHandleProps
			? this.eventHandlers.onClick
			: (event: MouseEvent<HTMLElement>) => {
					if (!snapshot.isDragging) {
						this.eventHandlers.onClick(event);
					}
				};

		return {
			...dragHandleProps,
			...this.eventHandlers,
			onClick,
			onMouseDown,
		};
	};

	renderCard = (cardProps: { isDragging?: boolean }) => {
		const { isInteractive, item, isVariableHeight } = this.props;
		const { isHovering, isFocused } = this.state;
		const isActive = Boolean(cardProps.isDragging) || this.state.isActive;
		return (
			// @ts-expect-error - TS2741 - Property 'isDraggable' is missing in type '{ children: ReactElement<any, string | JSXElementConstructor<any>>; isInteractive: boolean; isActive: boolean; isVariableHeight: boolean; isHovering: boolean; isFocused: boolean; isDragging?: boolean | undefined; }' but required in type 'Readonly<ThemedOuterStyledProps<ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & { isDragging: boolean; isInteractive: boolean; isVariableHeight: boolean; isDraggable: boolean; isHovering: boolean; isActive: boolean; }, any>>'.
			<Card
				{...cardProps}
				{...this.state}
				isInteractive={isInteractive}
				isActive={isActive}
				isVariableHeight={isVariableHeight}
			>
				{this.props.children(isHovering, isActive, isFocused, item)}
			</Card>
		);
	};

	renderDraggableCard() {
		return (
			<Draggable draggableId={this.props.item.id} index={this.props.index}>
				{(provided, snapshot) => (
					<DraggableCardContainer>
						{this.renderCard({
							innerRef: provided.innerRef,
							isDraggable: true,
							isDragging: snapshot.isDragging,
							...provided.draggableProps,
							...this.patchedHandlers(provided.dragHandleProps, snapshot),
						})}
					</DraggableCardContainer>
				)}
			</Draggable>
		);
	}

	render() {
		if (this.props.isReorderEnabled) {
			return this.renderDraggableCard();
		}
		// @ts-expect-error - TS2559: Type '{ onBlur: () => void; onClick: (arg1: MouseEvent ) => void; onDragEnd: () => void; onFocus: () => void; onMouseEnter: () => void; onMouseLeave: (arg1: MouseEvent...>) => void; onMouseDown: (arg1: MouseEvent...>) => void; onMouseUp: () => void; }' has no properties in common with type '{ isDragging?: boolean | undefined; }'.
		return this.renderCard({
			...this.eventHandlers,
		});
	}
}
